import { useEffect, useState } from 'react';

/**
 * "What am I looking at?" panel for the live map.
 *
 * A legend rather than a step-through tour: the map's conventions are not
 * obvious (a faded rose line is a patrol that ENDED, a dimmed pin belongs to a
 * finished patrol) and someone needs that answer on their tenth visit as much
 * as their first. A tour is dismissed once and gone.
 *
 * Auto-opens the first time per browser, then stays shut unless asked for. The
 * demo is where this earns its keep: a prospect lands on a map covered in
 * shapes with nobody sitting next to them to explain it.
 */
const SEEN_KEY = 'patrolit-legend-seen';

interface Row {
  swatch: React.ReactNode;
  label: string;
  desc: string;
}

function Line({ colour, opacity, width = 3, dashed = false }: {
  colour: string; opacity: number; width?: number; dashed?: boolean;
}) {
  return (
    <svg width="30" height="14" className="shrink-0" aria-hidden="true">
      <line
        x1="1" y1="7" x2="29" y2="7"
        stroke={colour} strokeOpacity={opacity} strokeWidth={width}
        strokeLinecap="round" strokeDasharray={dashed ? '5 4' : undefined}
      />
    </svg>
  );
}

function Dot({ bg, glyph, size = 22, opacity = 1 }: {
  bg: string; glyph: string; size?: number; opacity?: number;
}) {
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-full border-2 border-white"
      style={{
        background: bg, width: size, height: size, opacity,
        fontSize: size * 0.55, boxShadow: '0 1px 3px rgba(0,0,0,.35)',
      }}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

const ROWS: Row[] = [
  {
    swatch: <Line colour="#2563eb" opacity={1} width={4} />,
    label: 'Live patrol',
    desc: 'A patrol happening right now. Each patroller has their own colour, and the line grows as they walk.',
  },
  {
    swatch: <Line colour="#e11d48" opacity={0.25} />,
    label: 'Last 24 hours',
    desc: 'Routes of patrols that have already finished, kept for a day so you can see what has been covered and what has not.',
  },
  {
    swatch: <Dot bg="#dc2626" glyph="🚨" />,
    label: 'Alarm',
    desc: 'A patroller pressed panic. Click the pin for their description and, once acknowledged, the control room\'s response.',
  },
  {
    swatch: <Dot bg="#f59e0b" glyph="⚠️" size={20} />,
    label: 'Incident',
    desc: 'Something worth reporting but not an emergency — a broken light, an open gate, a suspicious vehicle.',
  },
  {
    swatch: <Dot bg="#f59e0b" glyph="⚠️" size={20} opacity={0.45} />,
    label: 'Dimmed pin',
    desc: 'Raised during a patrol that has since ended. Anything at full strength belongs to a patrol still running.',
  },
  {
    swatch: <Dot bg="#2563eb" glyph="🚁" />,
    label: 'Drone',
    desc: 'Position and flight path of a patrol drone, where a site has one. Blue dashes are its current flight.',
  },
];

export default function MapLegend() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) {
        setOpen(true);
        localStorage.setItem(SEEN_KEY, '1');
      }
    } catch {
      /* private browsing blocks localStorage — just don't auto-open */
    }
  }, []);

  return (
    <>
      {/* Sits above Leaflet's own panes (max z-index 700) but below the
          full-screen alarm modal, which must never be covered. */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="absolute top-3 right-3 z-[800] w-9 h-9 rounded-full bg-white border border-gray-300 shadow-md text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors font-semibold"
        title={open ? 'Hide map key' : 'What am I looking at?'}
        aria-expanded={open}
      >
        {open ? '×' : '?'}
      </button>

      {open && (
        <div className="absolute top-14 right-3 z-[800] w-80 max-w-[calc(100vw-1.5rem)] max-h-[70%] overflow-y-auto rounded-xl bg-white border border-gray-200 shadow-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-1">Map key</h3>
          <p className="text-xs text-gray-500 mb-4">
            Click any pin to read the patroller's own note.
          </p>
          <ul className="space-y-3">
            {ROWS.map((r) => (
              <li key={r.label} className="flex gap-3">
                <span className="mt-0.5 w-8 flex justify-center">{r.swatch}</span>
                <div>
                  <div className="text-sm font-medium text-gray-800">{r.label}</div>
                  <div className="text-xs text-gray-600 leading-relaxed">{r.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
